import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AssistantCheckoutFlow } from '@/components/AssistantCheckoutFlow';
import { renderAssistantPageState } from '@/components/assistant/AssistantPageStates';
import { AssistantHeader } from '@/components/assistant/AssistantHeader';
import { AssistantLocalBanner } from '@/components/assistant/AssistantLocalBanner';
import { AssistantMessageList } from '@/components/assistant/AssistantMessageList';
import { AssistantProfileDraftBar } from '@/components/assistant/AssistantProfileDraftBar';
import { AssistantFreeTextBar } from '@/components/assistant/AssistantFreeTextBar';
import { useAssistantChat } from '@/hooks/useAssistantChat';
import { useVisualViewportComposer } from '@/hooks/useVisualViewportComposer';

const ASSISTANT_SHELL_CLASS =
  'flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden bg-gradient-to-b from-background to-secondary/20';

const ASSISTANT_SCROLL_CLASS = 'flex-1 min-h-0 overflow-y-auto overscroll-y-contain';

const AssistantPage = () => {
  const { businessId: routeBusinessId } = useParams();
  const navigate = useNavigate();
  const chat = useAssistantChat({ routeBusinessId, navigate });
  const { composerRef, composerStyle, composerHeight } = useVisualViewportComposer(
    chat.showFreeTextBar || chat.profileDraft !== null
  );

  const scrollPadBottom =
    composerHeight > 0 ? composerHeight + 12 : undefined;

  const pageState = renderAssistantPageState({
    businessIdReady: chat.businessIdReady,
    routeBid: chat.routeBid,
    businessId: chat.businessId,
    loading: chat.loading,
    loadError: chat.loadError,
    menuData: chat.menuData,
    navigate,
    onRetry: () => {
      chat.setLoadError(false);
      chat.setMenuReloadKey((k) => k + 1);
    },
  });
  if (pageState) return pageState;

  return (
    <div className={ASSISTANT_SHELL_CLASS}>
      <AssistantHeader
        navigate={navigate}
        routeBid={chat.routeBid}
        primaryColor={chat.primaryColor}
        cartCount={chat.getCartCount()}
        onOpenCheckout={chat.showCheckoutPreferencePrompt}
        onBrowseProducts={() => chat.handleMainChoice('order')}
      />

      <AssistantLocalBanner business={chat.menuData?.business} />

      <div
        className={ASSISTANT_SCROLL_CLASS}
        style={scrollPadBottom ? { paddingBottom: scrollPadBottom } : undefined}
      >
        <AssistantMessageList
          messages={chat.messages}
          messagesEndRef={chat.messagesEndRef}
          messagesBottomPad={chat.messagesBottomPad}
          primaryColor={chat.primaryColor}
          business={chat.menuData?.business}
          benefitsProgressSnapshot={chat.benefitsProgressSnapshot}
          getAssistantLineQty={chat.getAssistantLineQty}
          bumpAssistantLineQty={chat.bumpAssistantLineQty}
          getCartQtyForProductId={chat.getCartQtyForProductId}
          onPostAddonAdd={chat.handlePostAddonAdd}
          onCatalogProductAdd={chat.handleCatalogProductAdd}
          onSuggestedProductAdd={chat.handleSuggestedProductAdd}
          onOrderSummaryLineQty={chat.handleOrderSummaryLineQty}
          onOrderSummaryRemoveLine={chat.handleOrderSummaryRemoveLine}
        />
      </div>

      <AssistantCheckoutFlow
        open={chat.assistantCheckoutOpen}
        onClose={() => {
          chat.setAssistantCheckoutOpen(false);
          chat.setCheckoutBootstrap(null);
        }}
        checkoutBootstrap={chat.checkoutBootstrap}
        businessId={chat.businessId}
        publicMenuRouteBusinessId={chat.routeBid}
        business={chat.menuData?.business}
        cart={chat.cart}
        getCartTotal={chat.getCartTotal}
        clearCart={chat.clearCart}
        removeFromCart={chat.removeFromCart}
        updateQuantity={chat.updateQuantity}
        removeAddOnFromCartLine={chat.removeAddOnFromCartLine}
        navigate={navigate}
        primaryColor={chat.primaryColor}
      />

      {chat.profileDraft !== null ? (
        <AssistantProfileDraftBar
          profileDraft={chat.profileDraft}
          setProfileDraft={chat.setProfileDraft}
          business={chat.menuData?.business}
          primaryColor={chat.primaryColor}
          onSubmit={chat.submitProfileDraft}
          onCancel={() => chat.setProfileDraft(null)}
          dockRef={composerRef}
          dockStyle={composerStyle}
        />
      ) : (
        <AssistantFreeTextBar
          visible={chat.showFreeTextBar}
          onAssistantHome={chat.onAssistantHome}
          primaryColor={chat.primaryColor}
          freeTextInput={chat.freeTextInput}
          setFreeTextInput={chat.setFreeTextInput}
          freeTextInputRef={chat.freeTextInputRef}
          onSubmitFreeText={chat.submitFreeText}
          onMainChoice={chat.handleMainChoice}
          multilineCompose={chat.currentStep === 'know-order'}
          dockRef={composerRef}
          dockStyle={composerStyle}
        />
      )}
    </div>
  );
};

export default AssistantPage;
